import { Component, OnInit, ViewChild, ElementRef, AfterViewChecked, OnDestroy } from "@angular/core";
import { MessageService } from "primeng/api";
import { FileUpload } from "primeng/fileupload";
import { ChangeRequestChatService } from "../../services/change-request-chat.service";
import {
    ChangeRequest,
    ChangeRequestMessage,
    ChangeRequestParticipant,
    UserGroup,
} from "../../domain/change-request-chat.model";
import { environment } from "src/environments/environment";

@Component({
    selector: "app-change-request-discussion",
    templateUrl: "./change-request-discussion.component.html",
    styleUrls: ["./change-request-discussion.component.scss"],
    providers: [MessageService],
})
export class ChangeRequestDiscussionComponent implements OnInit, AfterViewChecked, OnDestroy {
    @ViewChild("chatContainer") private chatContainer!: ElementRef;
    @ViewChild("fileUploader") private fileUploader!: FileUpload;

    changeRequests: ChangeRequest[] = [];
    selectedChangeRequest: ChangeRequest | null = null;
    messages: ChangeRequestMessage[] = [];
    newMessage: string = "";
    loading: boolean = false;
    messagesLoading: boolean = false;
    sendingMessage: boolean = false;

    uploadedFiles: File[] = [];
    maxFileSize: number = 10 * 1024 * 1024; // 10MB
    invalidFileSize: boolean = false;

    isReadOnly: boolean = false;
    apiUrl: string = environment.apiWizardUrl;
    mobileView: 'list' | 'chat' = 'list';

    currentUserContactId: number = 0;
    isAdmin: boolean = false;
    isTcMember: boolean = false;

    imagePreviewVisible: boolean = false;
    imagePreviewSrc: string = "";

    // Status modal
    statusModalVisible: boolean = false;
    updatingStatus: boolean = false;
    selectedNewStatus: number | null = null;

    // Participants modal
    participantsModalVisible: boolean = false;
    participantsLoading: boolean = false;
    participants: ChangeRequestParticipant[] = [];
    availableContacts: ChangeRequestParticipant[] = [];
    availableContactsLoading: boolean = false;
    userGroups: UserGroup[] = [];
    userGroupsLoading: boolean = false;
    selectedContact: { label: string; ContactId: number } | null = null;
    selectedUserGroup: UserGroup | null = null;
    addingParticipant: boolean = false;
    addingUserGroup: boolean = false;
    removingParticipantId: number | null = null;

    private pollInterval: any = null;
    private readonly POLL_INTERVAL_MS = 3000;
    private shouldScrollToBottom: boolean = false;

    constructor(
        private changeRequestService: ChangeRequestChatService,
        private messageService: MessageService
    ) {}

    ngOnInit(): void {
        this.loadChangeRequests();
        this.loadCurrentUserInfo();
    }

    private loadCurrentUserInfo(): void {
        this.changeRequestService.getCurrentUserContactId().subscribe({
            next: (response) => {
                this.currentUserContactId = response.contactId;
                this.isAdmin = response.isAdmin;
                this.isTcMember = response.isTcMember;
            },
            error: () => {
                this.currentUserContactId = 0;
                this.isAdmin = false;
                this.isTcMember = false;
            }
        });
    }

    ngAfterViewChecked(): void {
        if (this.shouldScrollToBottom) {
            this.scrollToBottom();
            this.shouldScrollToBottom = false;
        }
    }

    ngOnDestroy(): void {
        this.stopPolling();
    }

    loadChangeRequests(): void {
        this.loading = true;
        this.changeRequestService.getOpenDiscussions().subscribe({
            next: (data) => {
                this.changeRequests = data;
                this.loading = false;
            },
            error: () => {
                this.messageService.add({
                    key: "error",
                    severity: "error",
                    summary: "Error",
                    detail: "Failed to load change requests",
                });
                this.loading = false;
            },
        });
    }

    selectChangeRequest(cr: ChangeRequest): void {
        this.selectedChangeRequest = cr;
        this.isReadOnly = cr.Status === 2 || cr.Status === 3;
        this.mobileView = 'chat';
        this.loadMessages(cr.Id);
        this.startPolling();
    }

    backToList(): void {
        this.mobileView = 'list';
        this.stopPolling();
    }

    loadMessages(changeRequestId: number, sinceId?: number): void {
        if (!sinceId) {
            this.messagesLoading = true;
        }
        this.changeRequestService.getMessages(changeRequestId, sinceId).subscribe({
            next: (data) => {
                if (sinceId && sinceId > 0) {
                    if (data.length > 0) {
                        this.messages = [...this.messages, ...data];
                        this.shouldScrollToBottom = true;
                    }
                } else {
                    this.messages = data;
                    this.messagesLoading = false;
                    this.shouldScrollToBottom = true;
                }
            },
            error: () => {
                this.messageService.add({
                    key: "error",
                    severity: "error",
                    summary: "Error",
                    detail: "Failed to load messages",
                });
                this.messagesLoading = false;
            },
        });
    }

    private startPolling(): void {
        this.stopPolling();
        this.pollInterval = setInterval(() => {
            if (this.selectedChangeRequest && this.messages.length > 0) {
                const lastMessageId = Math.max(...this.messages.map(m => m.Id));
                this.loadMessages(this.selectedChangeRequest.Id, lastMessageId);
            }
        }, this.POLL_INTERVAL_MS);
    }

    private stopPolling(): void {
        if (this.pollInterval) {
            clearInterval(this.pollInterval);
            this.pollInterval = null;
        }
    }

    sendMessage(): void {
        if (!this.newMessage.trim() || !this.selectedChangeRequest || this.isReadOnly) {
            return;
        }

        this.sendingMessage = true;
        const formData = new FormData();
        formData.append("ChangeRequestId", this.selectedChangeRequest.Id.toString());
        formData.append("Message", this.newMessage);

        if (this.uploadedFiles.length > 0) {
            for (const file of this.uploadedFiles) {
                formData.append("Files", file);
            }
        }

        this.changeRequestService.addMessage(formData).subscribe({
            next: (data) => {
                this.messages.push(data);
                this.newMessage = "";
                this.uploadedFiles = [];
                this.invalidFileSize = false;
                this.sendingMessage = false;
                this.shouldScrollToBottom = true;
                if (this.fileUploader) {
                    this.fileUploader.clear();
                }
            },
            error: () => {
                this.messageService.add({
                    key: "error",
                    severity: "error",
                    summary: "Error",
                    detail: "Failed to send message",
                });
                this.sendingMessage = false;
            },
        });
    }

    deleteMessage(message: ChangeRequestMessage): void {
        if (!message.CanDelete) {
            return;
        }

        this.changeRequestService.deleteMessage(message.Id).subscribe({
            next: () => {
                const index = this.messages.findIndex((m) => m.Id === message.Id);
                if (index !== -1) {
                    this.messages[index].IsDeleted = true;
                }
            },
            error: () => {
                this.messageService.add({
                    key: "error",
                    severity: "error",
                    summary: "Error",
                    detail: "Failed to delete message",
                });
            },
        });
    }

    // ── Status modal ─────────────────────────────────────────────────────────

    openStatusModal(): void {
        if (!this.selectedChangeRequest) return;
        this.selectedNewStatus = this.selectedChangeRequest.Status;
        this.statusModalVisible = true;
    }

    closeStatusModal(): void {
        this.statusModalVisible = false;
        this.selectedNewStatus = null;
    }

    confirmStatusChange(): void {
        if (!this.selectedChangeRequest || this.selectedNewStatus === null) return;
        if (this.selectedNewStatus === this.selectedChangeRequest.Status) {
            this.closeStatusModal();
            return;
        }

        this.updatingStatus = true;
        this.changeRequestService.updateStatus(this.selectedChangeRequest.Id, this.selectedNewStatus).subscribe({
            next: (updated) => {
                const idx = this.changeRequests.findIndex(cr => cr.Id === updated.Id);
                if (idx !== -1) {
                    this.changeRequests[idx].Status = updated.Status;
                }
                this.selectedChangeRequest!.Status = updated.Status;
                this.isReadOnly = updated.Status === 2 || updated.Status === 3;
                this.updatingStatus = false;
                this.closeStatusModal();
            },
            error: () => {
                this.messageService.add({
                    key: "error",
                    severity: "error",
                    summary: "Error",
                    detail: "Failed to update status",
                });
                this.updatingStatus = false;
            },
        });
    }

    // ── Participants modal ────────────────────────────────────────────────────

    openParticipantsModal(): void {
        if (!this.selectedChangeRequest) return;
        this.participantsModalVisible = true;
        this.selectedContact = null;
        this.selectedUserGroup = null;
        this.loadParticipants();
        this.loadAvailableContacts();
        if (this.isAdmin) {
            this.loadUserGroups();
        }
    }

    closeParticipantsModal(): void {
        this.participantsModalVisible = false;
    }

    private loadParticipants(): void {
        if (!this.selectedChangeRequest) return;
        this.participantsLoading = true;
        this.changeRequestService.getParticipants(this.selectedChangeRequest.Id).subscribe({
            next: (data) => {
                this.participants = data;
                this.participantsLoading = false;
            },
            error: () => {
                this.participantsLoading = false;
            }
        });
    }

    private loadAvailableContacts(): void {
        if (!this.selectedChangeRequest) return;
        this.availableContactsLoading = true;
        this.changeRequestService.getAvailableContacts(this.selectedChangeRequest.Id).subscribe({
            next: (data) => {
                this.availableContacts = data;
                this.availableContactsLoading = false;
            },
            error: () => {
                this.availableContactsLoading = false;
            }
        });
    }

    private loadUserGroups(): void {
        this.userGroupsLoading = true;
        this.changeRequestService.getUserGroups().subscribe({
            next: (data) => {
                this.userGroups = data;
                this.userGroupsLoading = false;
            },
            error: () => {
                this.userGroupsLoading = false;
            }
        });
    }

    get contactOptions(): { label: string; ContactId: number }[] {
        return this.availableContacts.map(c => ({
            label: `${c.FullName} (${c.CompanyName})`,
            ContactId: c.ContactId
        }));
    }

    addParticipant(): void {
        if (!this.selectedChangeRequest || !this.selectedContact) return;
        this.addingParticipant = true;
        this.changeRequestService.addParticipant(this.selectedChangeRequest.Id, this.selectedContact.ContactId).subscribe({
            next: () => {
                this.addingParticipant = false;
                this.selectedContact = null;
                this.loadParticipants();
                this.loadAvailableContacts();
            },
            error: () => {
                this.messageService.add({
                    key: "error",
                    severity: "error",
                    summary: "Error",
                    detail: "Failed to add participant",
                });
                this.addingParticipant = false;
            }
        });
    }

    addUserGroup(): void {
        if (!this.selectedChangeRequest || !this.selectedUserGroup) return;
        this.addingUserGroup = true;
        this.changeRequestService.addUserGroup(this.selectedChangeRequest.Id, this.selectedUserGroup.UserGroupID).subscribe({
            next: () => {
                this.addingUserGroup = false;
                this.selectedUserGroup = null;
                this.loadParticipants();
                this.loadAvailableContacts();
            },
            error: () => {
                this.messageService.add({
                    key: "error",
                    severity: "error",
                    summary: "Error",
                    detail: "Failed to add user group",
                });
                this.addingUserGroup = false;
            }
        });
    }

    removeParticipant(contactId: number): void {
        if (!this.selectedChangeRequest) return;
        this.removingParticipantId = contactId;
        this.changeRequestService.removeParticipant(this.selectedChangeRequest.Id, contactId).subscribe({
            next: () => {
                this.participants = this.participants.filter(p => p.ContactId !== contactId);
                this.loadAvailableContacts();
                this.removingParticipantId = null;
            },
            error: () => {
                this.messageService.add({
                    key: "error",
                    severity: "error",
                    summary: "Error",
                    detail: "Failed to remove participant",
                });
                this.removingParticipantId = null;
            }
        });
    }

    // ── File handling ─────────────────────────────────────────────────────────

    onFileSelect(event: any): void {
        this.invalidFileSize = false;
        this.uploadedFiles = [];

        for (const file of event.currentFiles) {
            if (file.size > this.maxFileSize) {
                this.invalidFileSize = true;
                this.messageService.add({
                    key: "error",
                    severity: "error",
                    summary: "File too large",
                    detail: "Maximum file size is 10MB",
                });
                return;
            }
            this.uploadedFiles.push(file);
        }
    }

    removeFile(file: File): void {
        const index = this.uploadedFiles.indexOf(file);
        if (index !== -1) {
            this.uploadedFiles.splice(index, 1);
        }
    }

    downloadAttachment(messageId: number, fileName: string, contentType: string, originalFileName: string): void {
        this.changeRequestService.downloadFile(messageId, fileName).subscribe({
            next: (blob) => {
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement("a");
                link.href = url;
                link.download = originalFileName || fileName;
                link.click();
                window.URL.revokeObjectURL(url);
            },
            error: () => {
                this.messageService.add({
                    key: "error",
                    severity: "error",
                    summary: "Error",
                    detail: "Failed to download file",
                });
            },
        });
    }

    isImage(contentType: string): boolean {
        return contentType === "image/jpeg" || contentType === "image/png";
    }

    openImagePreview(messageId: number, fileName: string, originalFileName: string): void {
        this.imagePreviewSrc = this.getImagePreviewUrl(messageId, fileName);
        this.imagePreviewVisible = true;
    }

    closeImagePreview(): void {
        this.imagePreviewVisible = false;
        this.imagePreviewSrc = "";
    }

    isPdf(contentType: string): boolean {
        return contentType === "application/pdf";
    }

    getImagePreviewUrl(messageId: number, fileName: string): string {
        const base = `${this.apiUrl}api/ChangeRequestChat/DownloadFile`;
        const params = `messageId=${encodeURIComponent(messageId)}&fileName=${encodeURIComponent(fileName)}`;
        return `${base}?${params}`;
    }

    formatFileSize(bytes: number): string {
        if (bytes === 0) return "0 Bytes";
        const k = 1024;
        const sizes = ["Bytes", "KB", "MB", "GB"];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
    }

    formatDate(date: Date | string): string {
        const d = new Date(date);
        return d.toLocaleDateString() + " " + d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    }

    truncateDescription(description: string, maxLength: number = 80): string {
        if (!description) return "";
        if (description.length <= maxLength) return description;
        return description.substring(0, maxLength) + "...";
    }

    getStatusLabel(status: number): string {
        switch (status) {
            case 0: return "New";
            case 1: return "In Discussion";
            case 2: return "Approved";
            case 3: return "Rejected";
            default: return "Unknown";
        }
    }

    getStatusClass(status: number): string {
        switch (status) {
            case 0: return "status-new";
            case 1: return "status-discussion";
            case 2: return "status-approved";
            case 3: return "status-rejected";
            default: return "";
        }
    }

    private scrollToBottom(): void {
        if (this.chatContainer) {
            try {
                this.chatContainer.nativeElement.scrollTop = this.chatContainer.nativeElement.scrollHeight;
            } catch (err) {}
        }
    }
}
